import React, { FC, useCallback, useEffect, useMemo, useReducer, useRef } from "react";

import * as api from "api/datatable";
import { DatatableFocusState } from "types/Focus";
import { Pojo } from "types/Galaxy";
import { CancelTokenSource } from "axios";
import { mapToRSQL } from "utils/query.utils";
import { GSBuilder } from "utils/query.utils";
import { GS } from "utils/query.utils";
import { RSQLCriteria } from "rsql-criteria-typescript";
import { DatatableSort } from "composants/datatable/Table";
import { TypeSimpleComponent } from "enum";
import { uuidv4 } from "utils/uuid.utils";
import toaster from "composants/toaster/toaster";
import { ComponentState } from "types/Component";
import { FilterBar, PagedResource } from "types/Search";
import { ColumnMovedEvent, ColumnResizedEvent, FilterModel, RowClickedEvent, SortModelItem } from "ag-grid-community";
import { NotificationGroup } from "@axin-org/comet";
import AgGridContainer, {
  AGFilter,
  Filtermodel,
  mapcriteriaForGS,
  mapOneFilter,
  MIN_BATCH_SIZE
} from "./AgGridContainer";
import { AgGridReact } from "ag-grid-react";
import produce from "immer";
import { sortModelToOrderBy } from "./utils";

interface ReadOnlyTableProps {
  sjmoCode: string;
  tableName: string;
  ctrlkey: string;
  nodeType?: string;
  processIdDrop?: string;
  additionnalClause?: RSQLCriteria;
  onAfterSaveDatatable?: () => void;
  onAfterDeleteDatatable?: () => void;
  onAfterProcessListeGeneric?: () => void;
  onRowClickParent?(id: number | string): void;
}

interface DatatableAGGridDefinitionState {
  focus: DatatableFocusState[];
  columns: ComponentState[];
  selectedFocus: string | null;
}

type DatatableAGGridAction =
  | {
      type: "FETCH_DEFINITIONS";
      payload: { focus: DatatableFocusState[]; columns: ComponentState[]; selectedFocus: string };
    }
  | {
      type: "CHANGE_FOCUS";
      payload: { selectedFocus: string; columns: ComponentState[] };
    };

export async function loadDatatableDefinition(
  sjmoCode: string,
  tableName: string,
  ctrlKey: string
): Promise<[DatatableFocusState[], ComponentState[]]> {
  try {
    const focusRes = await api.getFocus(sjmoCode, tableName, ctrlKey);

    let columns: ComponentState[] = [];
    if (focusRes.data.length > 0) {
      const columnRes = await api.getColumn(sjmoCode, focusRes.data[0].focusId);
      columns = columnRes.data;
    }

    return [focusRes.data as DatatableFocusState[], columns];
  } catch (e) {
    return [[], []];
  }
}

export const initialDTState: DatatableAGGridDefinitionState = {
  focus: [],
  columns: [],
  selectedFocus: null
};
export function DTReducer(state: DatatableAGGridDefinitionState, action: DatatableAGGridAction) {
  switch (action.type) {
    case "FETCH_DEFINITIONS":
      return {
        ...state,
        focus: action.payload.focus,
        columns: action.payload.columns,
        selectedFocus: action.payload.selectedFocus
      };
    case "CHANGE_FOCUS":
      return {
        ...state,
        selectedFocus: action.payload.selectedFocus,
        columns: action.payload.columns
      };
    default:
      break;
  }
  return state;
}

/**
 * Async Fonction chargé de récupérer les données de la table via un appel au backend
 */
export async function fetchData(props: {
  sjmoCode: string;
  tableName: string;
  ctrlkey: string;
  currentFocus?: DatatableFocusState;
  query: string;
  startIndex: number;
  stopIndex: number;
  reset: boolean;
  cancelToken: CancelTokenSource;
  oelColumns: string[];
  filterBar: FilterBar | undefined;
  groupBy?: string;
}): Promise<PagedResource<Pojo>> {
  const {
    sjmoCode,
    tableName,
    ctrlkey,
    currentFocus,
    query,
    startIndex,
    stopIndex,
    reset,
    cancelToken,
    oelColumns,
    filterBar,
    groupBy
  } = props;

  if (!currentFocus || reset) {
    return { data: [], meta: { totalRecords: 0, size: 0 } };
  }
  const size = Math.max(stopIndex - startIndex, MIN_BATCH_SIZE);

  try {
    if (groupBy) {
      const { data } = await api.findAllWithGroupBy(
        {
          sjmoCode,
          tableName,
          filter: query,
          first: startIndex,
          size,
          includeJoinParent: true,
          includeOels: oelColumns,
          includeStyle: true,
          includeRowClassName: currentFocus?.cssClass,
          contextKey: ctrlkey,
          filterBar: filterBar || undefined,
          groupBy
        },
        cancelToken
      );

      return data;
    } else {
      const { data } = await api.findAll(
        {
          sjmoCode,
          tableName,
          filter: query,
          first: startIndex,
          size,
          includeJoinParent: true,
          includeOels: oelColumns,
          includeStyle: true,
          includeRowClassName: currentFocus?.cssClass,
          contextKey: ctrlkey,
          filterBar: filterBar || undefined,
          groupBy
        },
        cancelToken
      );
      return data;
    }
  } catch (e) {
    toaster.notify({
      id: uuidv4(),
      group: NotificationGroup.DEFAULT,
      title: "Erreur lors de la récupération des données",
      priority: "NORMAL",
      intent: "DANGER",
      createdAt: new Date().toISOString()
    });
    return { data: [], meta: { totalRecords: 0, size: 0 } };
  }
}

/**
 * Fonction qui transforme les filtres AG Grid en RSQL
 * searchvalues est de la forme 
 {
    "ldclDesignation": {
        "filterType": "text",
        "operator": "AND",
        "condition1": {
            "filterType": "text",
            "type": "contains",
            "filter": "defr"
        },
        "condition2": {
            "filterType": "text",
            "type": "contains",
            "filter": "sdf"
        },
        "conditions": [
            {
                "filterType": "text",
                "type": "contains",
                "filter": "defr"
            },
            {
                "filterType": "text",
                "type": "contains",
                "filter": "sdf"
            }
        ]
    },
    "articleId": {
        "filterType": "text",
        "type": "contains",
        "filter": "ppp"
    }
  }
 */
export function getRSQLFilter(
  interactions: Record<string, any>,
  searchValues: AGFilter,
  sortValues: Record<string, DatatableSort>,
  breakColumns: string[],
  additionnalClause: RSQLCriteria | undefined,
  columns: Record<string, ComponentState>
): RSQLCriteria {
  const searchFields = Object.keys(searchValues);

  const criteriaList: GS[] = [];
  for (let field of searchFields) {
    const col = columns[field.split(".")[0]];
    if (!col) continue;

    if (searchValues[field]) {
      const searchValue = searchValues[field];
      let criteria: GS | undefined;

      if (searchValue.operator === "AND" || searchValue.operator === "OR") {
        let conditions: GS[] = [];
        // Cas avec plusieurs conditions

        if (col.typeCompo === TypeSimpleComponent.AUTO_COMPLETE) {
          conditions = (searchValue as {
            conditions: Filtermodel[];
          }).conditions.map(condition => {
            if (condition.type !== "blank") {
              return mapcriteriaForGS(col, condition, field);
            } else {
              return mapOneFilter(condition, field);
            }
          });

        } else {
          conditions = (searchValue as {
            conditions: Filtermodel[];
          }).conditions.map(condition => mapOneFilter(condition, field));
        }

        criteria =
          searchValue.operator === "AND"
            ? GSBuilder.AND(...conditions)
            : GSBuilder.OR(...conditions);
      } else {
        // Cas avec une seule condition
        if (col.typeCompo === TypeSimpleComponent.AUTO_COMPLETE && searchValue.type !== "blank") {
          criteria = mapcriteriaForGS(col, searchValue, field);
        } else {
          criteria = mapOneFilter(searchValue, field);
        }
      }
      criteriaList.push(criteria);
    }
  }
  // on transforme l'interaction en rsql
  const rsql = mapToRSQL(interactions, sortValues, breakColumns);

  // on ajoute les filters à notre rsql
  const filtered = GSBuilder.toFilter(GSBuilder.AND(...criteriaList));
  rsql.filters.and(filtered);

  if (additionnalClause) {
    rsql.and(additionnalClause);
  }

  return rsql;
}

const ReadOnlyTable: FC<ReadOnlyTableProps> = ({
  sjmoCode,
  tableName,
  ctrlkey,
  nodeType,
  processIdDrop,
  additionnalClause,
  onRowClickParent
}) => {
  const gridRef = useRef<AgGridReact>(null);
  const [definition, dispatchDefinition] = useReducer(DTReducer, initialDTState);

  const currentFocus = useMemo(
    () => definition.focus.find(f => f.focusId === definition.selectedFocus),
    [definition.focus, definition.selectedFocus]
  );

  useEffect(() => {
    if (definition.focus.length === 0) {
      loadDatatableDefinition(sjmoCode, tableName, ctrlkey).then(([focus, columns]) => {
        dispatchDefinition({
          type: "FETCH_DEFINITIONS",
          payload: { focus, columns, selectedFocus: focus[0].focusId }
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sjmoCode, ctrlkey, tableName]);

  const onFocusChange = useCallback(
    async (selected: string) => {
      const columnRes = await api.getColumn(sjmoCode, selected);

      dispatchDefinition({
        type: "CHANGE_FOCUS",
        payload: {
          selectedFocus: selected,
          columns: columnRes.data
        }
      });
    },
    [sjmoCode]
  );

  /**
   * Gestion du déplacement des colonnes
   * L'évènement est envoyé pluseieurs fois lors du drag and drop.
   * On n'enregistre le déplacement qu'au moment où l'utilisateur lache la colonne IE l'index est le même que le précédent.
   */
  const movingColumnIndexRef = useRef<number | null>(null);
  const onColumnMoved = useCallback(
    (event: ColumnMovedEvent) => {
      const columnField = event.column?.getColId();

      // +1 pour la selection et +1 pour les satellites
      const newIndex = (event.toIndex as number) - 2;
      if (columnField && currentFocus && movingColumnIndexRef.current !== newIndex) {
        movingColumnIndexRef.current = newIndex;
      } else if (columnField && currentFocus && movingColumnIndexRef.current === newIndex) {
        // Récupérer la colonne déplacée et son ancien index
        const movedColumn = definition.columns.find(c => c.column === columnField);
        const oldIndex = definition.columns.findIndex(c => c.column === columnField);

        if (movedColumn && oldIndex !== -1) {
          // Créer une copie du tableau des colonnes
          const updatedColumns = [...definition.columns];

          // Retirer la colonne de son ancien index
          updatedColumns.splice(oldIndex, 1);

          // Insérer la colonne à son nouvel index
          updatedColumns.splice(newIndex, 0, movedColumn);

          // Dispatcher la nouvelle définition
          dispatchDefinition({
            type: "CHANGE_FOCUS",
            payload: {
              selectedFocus: currentFocus.focusId,
              columns: updatedColumns
            }
          });
        }
      }
    },
    [currentFocus, definition.columns]
  );

  const onColumnResized = useCallback(
    (params: ColumnResizedEvent) => {
      const columnName = params.column?.getColId();
      const width = params.column?.getActualWidth();

      if (!columnName || !width || !params.finished) return;

      if (currentFocus) {
        const updatedColumns = produce(definition.columns, draft => {
          const column = draft.find(c => c.column === columnName);
          if (column) {
            column.contentSize = width;
            column.labelSize = width;
            console.log("log temporaire", column.column, width);
          }
        });

        dispatchDefinition({
          type: "CHANGE_FOCUS",
          payload: {
            selectedFocus: currentFocus?.focusId,
            columns: updatedColumns
          }
        });
      }
    },
    [currentFocus, definition.columns]
  );

   const onSaveFocusPerso = useCallback(async ({
    currentOrderBy
  }: {
    currentFilter?: FilterModel;
    currentOrderBy?: SortModelItem[];
  }) => {
    if (currentFocus) {
      try {
        const orderBy = sortModelToOrderBy(currentOrderBy ?? []);
        await api.saveFocusPersoDatatable(
          currentFocus.focusId,
          tableName,
          ctrlkey,
          sjmoCode,
          definition.columns,
          [],
          orderBy || undefined
        );
        if (sessionStorage.getItem("superUser") !== "true") {
          const [newFocus, newColumns] = await loadDatatableDefinition(
            sjmoCode,
            tableName,
            ctrlkey
          );
          dispatchDefinition({
            type: "FETCH_DEFINITIONS",
            payload: { focus: newFocus, columns: newColumns, selectedFocus: newFocus[0].focusId }
          });
        }
      } catch (error) {
        console.error(
          "error during the save of the modified focus ",
          currentFocus.focusCode,
          " of the datatable ",
          ctrlkey
        );
        console.error(error);
      }
    }
  }, [currentFocus, definition.columns, ctrlkey, sjmoCode, tableName]);

  const onRazFocusPerso = useCallback(async () => {
    if (currentFocus) {
      await api.razFocusPersoDatatable(currentFocus.focusId, sjmoCode);
      const [newFocus, newColumns] = await loadDatatableDefinition(sjmoCode, tableName, ctrlkey);
      dispatchDefinition({
        type: "FETCH_DEFINITIONS",
        payload: { focus: newFocus, columns: newColumns, selectedFocus: newFocus[0].focusId }
      });
    }
  }, [ctrlkey, currentFocus, sjmoCode, tableName]);

  const onRowClickFunction = useCallback(
    (event: RowClickedEvent) => {
      onRowClickParent?.(event.data.id);
    },
    [onRowClickParent]
  );

  return (
    <AgGridContainer
      ref={gridRef}
      sjmoCode={sjmoCode}
      tableName={tableName}
      ctrlkey={ctrlkey}
      definition={definition}
      currentFocus={currentFocus}
      nodeType={nodeType}
      processIdDrop={processIdDrop}
      getRSQLFilter={getRSQLFilter}
      additionnalClause={additionnalClause}
      fetchData={fetchData}
      onSaveFocusPerso={onSaveFocusPerso}
      onRazFocusPerso={onRazFocusPerso}
      onFocusChange={onFocusChange}
      onColumnMoved={onColumnMoved}
      onRowClick={onRowClickFunction}
      onColumnResized={onColumnResized}
    />
  );
};

export default ReadOnlyTable;
